import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  LogOut, Lock, Receipt, Wallet, FolderKanban,
  Clock, AlertCircle,
  FileDown, AlertTriangle,
  LayoutDashboard, ReceiptIndianRupee,
} from "lucide-react";
import {
  clientMe, clientFinanceUnlock,
  clientFetchProjects, clientFetchInvoices, clientFetchSummary,
  clientFetchReceipts,
  clientFetchNonProject,
  clientFetchChangeRequests,
  apiErrorMsg,
} from "@/lib/api";

import { TOKEN_KEY, FIN_KEY, INR } from "@/components/client/clientUtils";
import { KpiGrid, TabButton } from "@/components/client/CommonUI";
import LoginScreen from "@/components/client/LoginScreen";
import FinanceUnlockModal from "@/components/client/FinanceUnlockModal";
import ClientDashboard from "@/components/client/ClientDashboard";
import ProjectsTable from "@/components/client/ProjectsTable";
import ChangeRequestsView from "@/components/client/ChangeRequestsView";
import InvoicesTable from "@/components/client/InvoicesTable";
import ReceiptsTable from "@/components/client/ReceiptsTable";
import AdditionalPaymentsTable from "@/components/client/AdditionalPaymentsTable";

const FINANCE_TABS = ["invoices", "additional", "receipts"];

export default function ClientPortal() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [client, setClient] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);

  useEffect(() => {
    if (!token) { setBootstrapping(false); return; }
    clientMe(token)
      .then(setClient)
      .catch(() => { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(FIN_KEY); setToken(""); })
      .finally(() => setBootstrapping(false));
  }, [token]);

  const onLoggedIn = (tok, cl) => {
    localStorage.setItem(TOKEN_KEY, tok);
    setToken(tok);
    setClient(cl);
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(FIN_KEY);
    setToken("");
    setClient(null);
  };

  if (bootstrapping) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7f6f3]">
        <div className="text-sm font-mono text-[#4a4a4a] animate-pulse">Loading portal…</div>
      </div>
    );
  }

  if (!token || !client) return <LoginScreen onLoggedIn={onLoggedIn} />;
  return <Dashboard token={token} client={client} onLogout={onLogout} />;
}

function Dashboard({ token, client, onLogout }) {
  const [tab, setTab] = useState("dashboard");
  const [financeToken, setFinanceToken] = useState(() => localStorage.getItem(FIN_KEY) || "");
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [pendingTab, setPendingTab] = useState("");
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [additional, setAdditional] = useState([]);
  const [changeRequests, setChangeRequests] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const dashName = client.dashboard_name || `${client.name} Dashboard`;

  const loadProjects = async () => {
    setLoading(true);
    try {
      const [projs, crs] = await Promise.all([
        clientFetchProjects(token),
        clientFetchChangeRequests(token).catch(() => []),
      ]);
      setProjects(projs);
      setChangeRequests(crs);
    } catch (e) { toast.error(apiErrorMsg(e, "Failed to load projects")); }
    finally { setLoading(false); }
  };

  const loadFinance = async () => {
    if (!financeToken) return;
    setLoading(true);
    try {
      const [inv, sum, rec, add] = await Promise.all([
        clientFetchInvoices(token, financeToken),
        clientFetchSummary(token, financeToken),
        clientFetchReceipts(token, financeToken),
        clientFetchNonProject(token, financeToken),
      ]);
      setInvoices(inv); setSummary(sum);
      setReceipts(rec); setAdditional(add);
    } catch (e) {
      if (e?.response?.status === 403) {
        localStorage.removeItem(FIN_KEY);
        setFinanceToken("");
        setUnlockOpen(true);
        toast.error("Finance unlock expired — please re-enter");
      } else {
        toast.error(apiErrorMsg(e, "Failed to load finance data"));
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { loadProjects(); }, []);
  useEffect(() => {
    if (financeToken && FINANCE_TABS.includes(tab)) loadFinance();
  }, [tab, financeToken]);

  const handleUnlock = async (password) => {
    try {
      const { finance_token } = await clientFinanceUnlock(token, password);
      localStorage.setItem(FIN_KEY, finance_token);
      setFinanceToken(finance_token);
      setUnlockOpen(false);
      if (pendingTab) { setTab(pendingTab); setPendingTab(""); }
      toast.success("Finance section unlocked");
    } catch (e) {
      toast.error(apiErrorMsg(e, "Wrong password"));
    }
  };

  const requestFinance = (targetTab) => {
    if (financeToken) { setTab(targetTab); return; }
    setPendingTab(targetTab);
    setUnlockOpen(true);
  };

  const financeKpis = useMemo(() => {
    if (!summary) return [];
    return [
      { label: "Total invoiced", value: INR(summary.total_invoiced), tone: "bg-indigo-500", icon: <Receipt className="h-4 w-4" /> },
      { label: "Total received", value: INR(summary.total_received), tone: "bg-emerald-500", accent: "text-emerald-700", icon: <Wallet className="h-4 w-4" /> },
      { label: "Total pending",  value: INR(summary.total_pending),  tone: "bg-amber-500", accent: "text-amber-700", icon: <AlertCircle className="h-4 w-4" /> },
      { label: "Open invoices",  value: summary.invoices_open,       tone: "bg-rose-500", icon: <Clock className="h-4 w-4" /> },
    ];
  }, [summary]);

  return (
    <div className="min-h-screen bg-[#f7f6f3]">
      {/* Top bar */}
      <header className="bg-[#0a0a0a] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <a href="/" className="flex items-center">
              <img src="/reachvel-logo-dark.png" alt="Reachvel" className="h-7 md:h-8 w-auto" />
            </a>
            <span className="h-8 w-px bg-white/20" />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#ff5722] font-bold">{client.name}</div>
              <div className="text-sm font-semibold leading-tight" data-testid="client-dashboard-name">{dashName}</div>
            </div>
          </div>
          <button onClick={onLogout} data-testid="client-logout"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-white/20 rounded-full hover:bg-white/10">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 pb-3 -mt-1 flex items-center justify-end gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-white/60">
          <span className="h-1.5 w-1.5 rounded-full bg-[#ff5722] animate-pulse" />
          <span data-testid="client-today">{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</span>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-black/10 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center gap-2 overflow-x-auto">
          <TabButton active={tab === "dashboard"} onClick={() => setTab("dashboard")} icon={<LayoutDashboard className="h-4 w-4" />} label="Dashboard" testid="client-tab-dashboard" />
          <TabButton active={tab === "projects"} onClick={() => setTab("projects")} icon={<FolderKanban className="h-4 w-4" />} label="Project Status" testid="client-tab-projects" />
          <TabButton active={tab === "change-requests"} onClick={() => setTab("change-requests")} icon={<AlertTriangle className="h-4 w-4" />} label="Change Requests" testid="client-tab-crs" />
          <TabButton active={tab === "invoices"} onClick={() => requestFinance("invoices")} icon={<Receipt className="h-4 w-4" />} label="Invoices" locked={!financeToken} testid="client-tab-invoices" />
          <TabButton active={tab === "additional"} onClick={() => requestFinance("additional")} icon={<ReceiptIndianRupee className="h-4 w-4" />} label="Additional Payments" locked={!financeToken} testid="client-tab-additional" />
          <TabButton active={tab === "receipts"} onClick={() => requestFinance("receipts")} icon={<FileDown className="h-4 w-4" />} label="Receipts" locked={!financeToken} testid="client-tab-receipts" />
          {financeToken && (
            <button onClick={() => {
              localStorage.removeItem(FIN_KEY);
              setFinanceToken("");
              setInvoices([]); setSummary(null); setReceipts([]); setAdditional([]);
              if (FINANCE_TABS.includes(tab)) setTab("dashboard");
              toast.info("Finance section locked");
            }}
              data-testid="client-lock-finance"
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.15em] text-[#4a4a4a] hover:text-rose-500">
              <Lock className="h-3 w-3" /> Lock finance
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {tab === "dashboard" && (
          <ClientDashboard
            projects={projects} changeRequests={changeRequests}
            loading={loading} onRefresh={loadProjects}
            onGoToTab={setTab}
          />
        )}
        {tab === "projects" && (
          <ProjectsTable rows={projects} loading={loading} onRefresh={loadProjects} token={token} />
        )}
        {tab === "change-requests" && (
          <ChangeRequestsView rows={changeRequests} loading={loading} onRefresh={loadProjects} token={token} projects={projects} clientId={client.id} />
        )}
        {tab === "invoices" && (
          <>
            <KpiGrid cards={financeKpis} />
            <InvoicesTable rows={invoices} loading={loading} onRefresh={loadFinance} projects={projects} token={token} financeToken={financeToken} />
          </>
        )}
        {tab === "additional" && (
          <>
            <KpiGrid cards={financeKpis} />
            <AdditionalPaymentsTable rows={additional} loading={loading} onRefresh={loadFinance} token={token} financeToken={financeToken} />
          </>
        )}
        {tab === "receipts" && (
          <>
            <KpiGrid cards={financeKpis} />
            <ReceiptsTable rows={receipts} loading={loading} onRefresh={loadFinance} token={token} financeToken={financeToken} />
          </>
        )}
      </main>

      {unlockOpen && <FinanceUnlockModal onClose={() => setUnlockOpen(false)} onSubmit={handleUnlock} />}
    </div>
  );
}
